import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, Trophy, Flag, Wallet, MessageCircle, Users } from 'lucide-react';
import Layout from '../components/Layout';
import EmptyState from '../components/EmptyState';
import { Card, SkeletonCard, IconBadge } from '../components/ui';
import { notificationsApi, AppNotification } from '../api';
import { parseSAST } from '../utils/datetime';

const iconFor = (type: string) => {
  switch (type) {
    case 'game_invite': return <Users />;
    case 'game_started': return <Flag />;
    case 'verify': return <Trophy />;
    case 'bet_settled': return <Wallet />;
    case 'message': return <MessageCircle />;
    default: return <Bell />;
  }
};

const timeAgo = (iso: string) => {
  const then = parseSAST(iso).getTime();
  const mins = Math.max(0, Math.round((Date.now() - then) / 60000));
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.round(hrs / 24)}d ago`;
};

const Notifications: React.FC = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState<AppNotification[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    notificationsApi.list()
      .then((data) => { if (active) setItems(Array.isArray(data) ? data : []); })
      .catch(() => {})
      .finally(() => { if (active) setLoading(false); });
    // Opening the page clears the unread badge.
    notificationsApi.markAllRead().catch(() => {});
    return () => { active = false; };
  }, []);

  return (
    <Layout>
      <div className="max-w-2xl mx-auto pb-24">
        <div className="flex items-center space-x-3 mb-6">
          <IconBadge tone="gold"><Bell /></IconBadge>
          <div>
            <h1 className="text-xl font-bold text-white">Notifications</h1>
            <p className="text-sm text-green-100/60">Invites, payouts, and fresh reasons to check the group chat.</p>
          </div>
        </div>

        {loading ? (
          <div className="space-y-3">
            <SkeletonCard />
            <SkeletonCard />
            <SkeletonCard />
          </div>
        ) : items.length === 0 ? (
          <Card>
            <EmptyState
              image="/images/empty-notifications.png"
              title="You're all caught up"
              subtitle="Invites, your turn to verify, and payouts will show up here."
            />
          </Card>
        ) : (
          <div className="space-y-2">
            {items.map((n) => (
              <button
                key={n.id}
                onClick={() => n.link && navigate(n.link)}
                className={`w-full text-left flex items-start space-x-3 p-4 rounded-xl border transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-400/70 ${
                  n.is_read ? 'bg-white border-gray-100' : 'bg-emerald-50 border-emerald-200'
                } ${n.link ? 'hover:border-emerald-300 cursor-pointer' : 'cursor-default'}`}
              >
                <IconBadge size="md" tone={n.type === 'verify' ? 'gold' : 'brand'}>{iconFor(n.type)}</IconBadge>
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-gray-800">{n.title}</p>
                  {n.body && <p className="text-sm text-gray-500 truncate">{n.body}</p>}
                  <p className="text-[11px] text-gray-400 mt-1">{timeAgo(n.created_at)}</p>
                </div>
                {!n.is_read && <span className="mt-2 w-2 h-2 rounded-full bg-emerald-500 flex-none" />}
              </button>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Notifications;
